import {useContext} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {useDecision} from '@s-ui/pde'
import Context from '@s-ui/react-context'
import {SaitamaAd} from '@adv-mt/advertising-publift'

import {
  getFirstTallAdPosition,
  getTallAdPositions,
  MIN_COLUMNS_WITH_TALL_AD,
  NEXT_PUBLI_POSITION,
  NEXT_PUBLI_POSITION_THREE_CARDS
} from './helpers.js'
import {snapFullWidthItems, TALL_AD_ROW_SPAN, TALL_AD_ROW_SPAN_THREE_CARDS} from './snapping.js'
import useGridColumns from './useGridColumns.js'

const BASE_CLASS = 'mt-ListAds-gridLayout'
const BASE_CLASS_GRID_ITEM = `${BASE_CLASS}-gridItem`

const GridLayout = ({listOfAds = [], getGridItem, pageName}) => {
  const {domain} = useContext(Context)

  const config = domain.get('config')
  const {NO_ADVERTISING_PAGES, PubRight1, PubRight2, ADS_LIST_POSITIONS} = config.get('advertising')
  const isMobile = config.get('isMobile')

  const pubRight1Id = ADS_LIST_POSITIONS?.[PubRight1]?.id
  const pubRight2Id = ADS_LIST_POSITIONS?.[PubRight2]?.id
  // Array of pub IDs to rotate through (add more IDs here in the future)
  const tallAdPubIds = [pubRight1Id, pubRight2Id].filter(Boolean)

  const hasAdvertising = !NO_ADVERTISING_PAGES.includes(pageName)

  // Seeded from the user agent so the first client render matches the server one. The media queries
  // only resolve after hydration, and this count decides both the tallAd position and the full-width
  // row, so an unseeded first render would reorder the whole grid once the effects run.
  const columnsCount = useGridColumns({defaultDesktop: !isMobile})
  const isTallAdVisible = columnsCount >= MIN_COLUMNS_WITH_TALL_AD

  const {enabled: isTallAdThreeCards} = useDecision('ff_web_buyer_ads_grid_ad_3_cards')
  const tallAdRowSpan = isTallAdThreeCards ? TALL_AD_ROW_SPAN_THREE_CARDS : TALL_AD_ROW_SPAN
  const nextTallAdPosition = isTallAdThreeCards ? NEXT_PUBLI_POSITION_THREE_CARDS : NEXT_PUBLI_POSITION

  // `_adSpace` entries are full-width rows injected on top of the card sequence the tallAd math is
  // tuned against, so they must not shift the count it relies on.
  const cards = listOfAds.filter(item => !item._adSpace)
  const tallAdPositions = hasAdvertising
    ? getTallAdPositions(cards.length, tallAdPubIds.length, getFirstTallAdPosition({columnsCount}), nextTallAdPosition)
    : []

  const items = snapFullWidthItems({listOfAds, columnsCount, tallAdPositions, isTallAdVisible, tallAdRowSpan})

  const tallAdClassName = cx(BASE_CLASS_GRID_ITEM, `${BASE_CLASS_GRID_ITEM}--tallAd`, {
    [`${BASE_CLASS_GRID_ITEM}--tallAdThreeCards`]: isTallAdThreeCards
  })

  let cardIndex = -1
  let tallAdIndex = 0

  return (
    <div className={BASE_CLASS}>
      {items.map((item, index) => {
        if (item._adSpace) {
          return (
            <div
              key={item.key || `adspace-${index}`}
              className={`${BASE_CLASS_GRID_ITEM} ${BASE_CLASS_GRID_ITEM}--fullWidth`}
            >
              {getGridItem(item, {columnsCount})}
            </div>
          )
        }

        cardIndex++

        const gridItem = (
          <div key={item.key || `card-${index}`} className={BASE_CLASS_GRID_ITEM}>
            {getGridItem(item, {columnsCount})}
          </div>
        )

        if (!tallAdPositions.includes(cardIndex)) return gridItem

        const pubId = tallAdPubIds[tallAdIndex++ % tallAdPubIds.length]

        return [
          gridItem,
          <div key={`tall-ad-${pubId}-${index}`} className={tallAdClassName}>
            <div className={`${BASE_CLASS_GRID_ITEM}-tallAdPlaceholder`}>
              <div className={`${BASE_CLASS_GRID_ITEM}-tallAdInner`}>
                <p className={`${BASE_CLASS_GRID_ITEM}-tallAdInnerText`}>Publicidad</p>
                <SaitamaAd id={pubId} />
              </div>
            </div>
          </div>
        ]
      })}
    </div>
  )
}

GridLayout.displayName = 'GridLayout'

GridLayout.propTypes = {
  listOfAds: PropTypes.array.isRequired,
  getGridItem: PropTypes.func.isRequired,
  pageName: PropTypes.string.isRequired
}

export default GridLayout
